import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"

import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import RadioGroup from "components/RadioGroup/RadioGroup"
import ToggleSwitch from "components/ToggleSwitch"
import {
    ACCESS_TYPE_OPTIONS,
    CSMS_PARTNER_OPTIONS,
    LOCATION_SUBTYPE_OPTIONS,
    LOCATION_TYPE_OPTIONS,
    OPENING_HOURS_OPTIONS,
} from "enums/dropdownValues"
import FileUploader from "pages/Projects/features/RaiseTicketForm/common/FileUploader/FileUploader"
import ProjectFormFooter from "../../components/ProjectFormFooter"
import ChargerDetailsTable from "./ChargerDetailsTable"
import useSaas, { ESaasFields, HOST_MANAGED_BY_OPTIONS } from "./useSaas"

import { faAngleDown, faAngleUp } from "@fortawesome/sharp-thin-svg-icons"
import { Tooltip } from "@mui/material"

const SaaS = (props: ProjectFormPageProps) => {
    const { states, functions } = useSaas(props)
    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Grid
                    container
                    spacing={2}
                    paddingBottom={4}
                    sx={{
                        borderBottom: "1px solid #0000001A",
                    }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                                <Typography variant="label">Select CPO</Typography>
                                <Tooltip
                                    placement="top"
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                maxWidth: "none",
                                            },
                                        },
                                    }}
                                    title="This field can only be updated from the 'Account and Site' page.">
                                    <FontAwesomeIcon icon={faCircleInfo} color="#A8A8A8" size="sm" />
                                </Tooltip>
                            </Box>
                            <MuiAutoComplete
                                disablePortal={false}
                                disabled
                                disableClearable
                                size="large"
                                placeholder="Select CPO"
                                options={states.cpoOptions}
                                loading={states.isCpoFetching}
                                value={states.formik.values[ESaasFields.CPO]}
                                onChange={value => states.formik.setFieldValue(ESaasFields.CPO, value)}
                                error={functions.hasError(ESaasFields.CPO)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">CSMS Partner*</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                disableClearable
                                size="large"
                                placeholder="Select CSMS Partner"
                                options={CSMS_PARTNER_OPTIONS}
                                value={states.formik.values[ESaasFields.CSMS_PARTNER]}
                                onChange={value => states.formik.setFieldValue(ESaasFields.CSMS_PARTNER, value)}
                                error={functions.hasError(ESaasFields.CSMS_PARTNER)}
                            />
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                SaaS Fee
                            </Typography>
                            <RadioGroup
                                options={states.yesNoOptions}
                                name={ESaasFields.SAAS_FEE}
                                onSelect={(value: any) => functions.handleRadioChange(ESaasFields.SAAS_FEE, value)}
                                value={functions.getRadioValue(ESaasFields.SAAS_FEE)}
                            />
                        </Box>
                    </Grid>
                    {Boolean(states.formik.values[ESaasFields.SAAS_FEE]) && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Amount*</Typography>
                                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                    <MuiTextField
                                        placeholder="Enter Amount"
                                        fullWidth
                                        type="number"
                                        size="large"
                                        value={states.formik.values[ESaasFields.DEAL_AMOUNT]}
                                        onChange={states.formik.handleChange}
                                        name={ESaasFields.DEAL_AMOUNT}
                                        error={functions.hasError(ESaasFields.DEAL_AMOUNT)}
                                    />
                                    <MuiTextField
                                        fullWidth
                                        placeholder="Increment %"
                                        type="number"
                                        size="large"
                                        value={states.formik.values[ESaasFields.DEAL_AMOUNT_ESC]}
                                        onChange={states.formik.handleChange}
                                        name={ESaasFields.DEAL_AMOUNT_ESC}
                                        error={functions.hasError(ESaasFields.DEAL_AMOUNT_ESC)}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">
                                License Fee{states.formik.values[ESaasFields.SAAS_FEE] ? "*" : ""}
                            </Typography>
                            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                <MuiTextField
                                    fullWidth
                                    placeholder="AC Charger"
                                    type="number"
                                    size="large"
                                    value={states.formik.values[ESaasFields.LICENSE_FEE_AC]}
                                    onChange={states.formik.handleChange}
                                    name={ESaasFields.LICENSE_FEE_AC}
                                    error={functions.hasError(ESaasFields.LICENSE_FEE_AC)}
                                />
                                <MuiTextField
                                    placeholder="DC Charger"
                                    fullWidth
                                    type="number"
                                    size="large"
                                    value={states.formik.values[ESaasFields.LICENSE_FEE_DC]}
                                    onChange={states.formik.handleChange}
                                    name={ESaasFields.LICENSE_FEE_DC}
                                    error={functions.hasError(ESaasFields.LICENSE_FEE_DC)}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Station Details */}
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Box
                        display={"flex"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        className="cursor-pointer"
                        onClick={() => functions.setShowStationDetails(!states.showStationDetails)}>
                        <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                            STATION DETAILS
                        </Typography>
                        <FontAwesomeIcon icon={states.showStationDetails ? faAngleUp : faAngleDown} color="#8B8B8B" />
                    </Box>
                    {states.showStationDetails && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Charging Station Name*</Typography>
                                    <MuiTextField
                                        placeholder="Enter Charging Station Name"
                                        size="large"
                                        value={states.formik.values[ESaasFields.STATION_NAME]}
                                        onChange={states.formik.handleChange}
                                        name={ESaasFields.STATION_NAME}
                                        error={functions.hasError(ESaasFields.STATION_NAME)}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={12}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Area</Typography>
                                    <MuiTextField
                                        placeholder="Enter Area Name"
                                        size="large"
                                        value={states.formik.values[ESaasFields.AREA]}
                                        onChange={states.formik.handleChange}
                                        name={ESaasFields.AREA}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={12}>
                                <Box display="flex" flexDirection="column" gap={1} sx={{ width: { xs: "100%", sm: "50%" } }}>
                                    <Typography variant="label">Country*</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        size="large"
                                        disableClearable
                                        placeholder="Select Country"
                                        options={states.countryOptions}
                                        loading={states.isCountriesFetching}
                                        value={states.formik.values[ESaasFields.COUNTRY]}
                                        onChange={(value: any) => functions.handleCountryChange(value)}
                                        error={functions.hasError(ESaasFields.COUNTRY)}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">State*</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        size="large"
                                        disableClearable
                                        placeholder={states.isStateDisabled ? "Select Country first" : "Select State"}
                                        options={states.stateOptions}
                                        loading={states.isStatesFetching}
                                        value={states.formik.values[ESaasFields.STATE]}
                                        onChange={(value: any) => functions.handleStateChange(value)}
                                        error={functions.hasError(ESaasFields.STATE)}
                                        disabled={states.isStateDisabled}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">City*</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder={states.isCityDisabled ? "Select State first" : "Select City"}
                                        options={states.cityOptions}
                                        loading={states.isCitiesFetching}
                                        value={states.formik.values[ESaasFields.CITY]}
                                        onChange={value => states.formik.setFieldValue(ESaasFields.CITY, value)}
                                        error={functions.hasError(ESaasFields.CITY)}
                                        disabled={states.isCityDisabled}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Pincode</Typography>
                                    <MuiTextField
                                        placeholder="Enter Pincode"
                                        type="number"
                                        size="large"
                                        value={states.formik.values[ESaasFields.PINCODE]}
                                        onChange={states.formik.handleChange}
                                        name={ESaasFields.PINCODE}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Landmark</Typography>
                                    <MuiTextField
                                        placeholder="Enter Landmark"
                                        size="large"
                                        value={states.formik.values[ESaasFields.LANDMARK]}
                                        onChange={states.formik.handleChange}
                                        name={ESaasFields.LANDMARK}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Latitude*</Typography>
                                    <MuiTextField
                                        placeholder="Enter Latitude"
                                        type="number"
                                        size="large"
                                        value={states.formik.values[ESaasFields.LATITUDE]}
                                        onChange={states.formik.handleChange}
                                        name={ESaasFields.LATITUDE}
                                        error={functions.hasError(ESaasFields.LATITUDE)}
                                        onPaste={e => functions.handleCoordinatePaste(e, "latitude")}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Longitude*</Typography>
                                    <MuiTextField
                                        placeholder="Enter Longitude"
                                        type="number"
                                        size="large"
                                        value={states.formik.values[ESaasFields.LONGITUDE]}
                                        onChange={states.formik.handleChange}
                                        name={ESaasFields.LONGITUDE}
                                        error={functions.hasError(ESaasFields.LONGITUDE)}
                                        onPaste={e => functions.handleCoordinatePaste(e, "longitude")}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Amenities</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder="Select Amenities"
                                        options={states.amenityOptions}
                                        loading={states.isAmenitiesFetching}
                                        value={states.formik.values[ESaasFields.AMENITIES]}
                                        onChange={value => states.formik.setFieldValue(ESaasFields.AMENITIES, value)}
                                        error={functions.hasError(ESaasFields.AMENITIES)}
                                        multiple
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Access Type*</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder="Select Access Type"
                                        options={ACCESS_TYPE_OPTIONS}
                                        value={states.formik.values[ESaasFields.ACCESS_TYPE]}
                                        onChange={value => states.formik.setFieldValue(ESaasFields.ACCESS_TYPE, value)}
                                        error={functions.hasError(ESaasFields.ACCESS_TYPE)}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={12}>
                                <Box display="flex" flexDirection="column" gap={1} sx={{ width: { xs: "100%", sm: "50%" } }}>
                                    <Typography
                                        variant="label"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            justifyContent: "space-between",
                                        }}>
                                        Co-Branding Partner
                                        <ToggleSwitch
                                            checked={states.formik.values[ESaasFields.CO_BRANDING_ENABLED]}
                                            toggleSwitch={() =>
                                                states.formik.setFieldValue(
                                                    ESaasFields.CO_BRANDING_ENABLED,
                                                    !states.formik.values[ESaasFields.CO_BRANDING_ENABLED]
                                                )
                                            }
                                        />
                                    </Typography>
                                    {Boolean(states.formik.values[ESaasFields.CO_BRANDING_ENABLED]) && (
                                        <MuiAutoComplete
                                            disablePortal={false}
                                            size="large"
                                            placeholder="Select Co-Branding Partner"
                                            options={states.coBrandedOptions}
                                            loading={states.isCoBrandedFetching}
                                            value={states.formik.values[ESaasFields.CO_BRANDING_PARTNER]}
                                            onChange={value =>
                                                states.formik.setFieldValue(ESaasFields.CO_BRANDING_PARTNER, value)
                                            }
                                        />
                                    )}
                                </Box>
                            </Grid>

                            <Grid size={12}>
                                <Box display="flex" flexDirection="column" gap={1} sx={{ width: { xs: "100%", sm: "50%" } }}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        Opening Hours*
                                    </Typography>
                                    <RadioGroup
                                        options={OPENING_HOURS_OPTIONS}
                                        name={ESaasFields.OPENING_HOURS}
                                        onSelect={(value: unknown) =>
                                            states.formik.setFieldValue(ESaasFields.OPENING_HOURS, value)
                                        }
                                        value={states.formik.values[ESaasFields.OPENING_HOURS]}
                                    />
                                </Box>
                            </Grid>

                            {states.formik.values[ESaasFields.OPENING_HOURS]?.title === "Custom" && (
                                <>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <Typography variant="label">Open Time</Typography>
                                            <MuiTextField
                                                placeholder="HH:MM"
                                                type="time"
                                                size="large"
                                                value={states.formik.values[ESaasFields.OPEN_TIME]}
                                                onChange={states.formik.handleChange}
                                                name={ESaasFields.OPEN_TIME}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <Typography variant="label">Close Time</Typography>
                                            <MuiTextField
                                                placeholder="HH:MM"
                                                type="time"
                                                size="large"
                                                value={states.formik.values[ESaasFields.CLOSE_TIME]}
                                                onChange={states.formik.handleChange}
                                                name={ESaasFields.CLOSE_TIME}
                                            />
                                        </Box>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}
                </Box>

                {/* Location Details */}
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Box
                        display={"flex"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        className="cursor-pointer"
                        onClick={() => functions.setShowLocationDetails(!states.showLocationDetails)}>
                        <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                            LOCATION DETAILS
                        </Typography>
                        <FontAwesomeIcon icon={states.showLocationDetails ? faAngleUp : faAngleDown} color="#8B8B8B" />
                    </Box>
                    {states.showLocationDetails && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Location Type*</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        size="large"
                                        placeholder="Select Location Type"
                                        options={LOCATION_TYPE_OPTIONS}
                                        value={states.formik.values[ESaasFields.LOCATION_TYPE]}
                                        onChange={value =>
                                            states.formik.setFieldValue(ESaasFields.LOCATION_TYPE, value)
                                        }
                                        error={functions.hasError(ESaasFields.LOCATION_TYPE)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Location Subtype*</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        size="large"
                                        placeholder="Select Location Subtype"
                                        options={LOCATION_SUBTYPE_OPTIONS}
                                        value={states.formik.values[ESaasFields.LOCATION_SUBTYPE]}
                                        onChange={value =>
                                            states.formik.setFieldValue(ESaasFields.LOCATION_SUBTYPE, value)
                                        }
                                        error={functions.hasError(ESaasFields.LOCATION_SUBTYPE)}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Box>

                {states.isFranchisePartner && (
                    <Box
                        display={"flex"}
                        flexDirection={"column"}
                        gap={2}
                        bgcolor={"#FAFAFA"}
                        borderRadius={"8px"}
                        padding={"16px"}>
                        <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                            FRANCHISE DETAILS
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Franchise Name (auto)</Typography>
                                    <MuiTextField
                                        fullWidth
                                        size="large"
                                        disabled
                                        value={states.formik.values[ESaasFields.FRANCHISE].franchise_name}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Host Managed By</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder="Select"
                                        options={HOST_MANAGED_BY_OPTIONS}
                                        value={states.formik.values[ESaasFields.FRANCHISE].host_managed_by}
                                        onChange={value =>
                                            functions.handleFranchiseFieldChange("host_managed_by", value as any)
                                        }
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">SaaS Fee - AC (₹/kWh)</Typography>
                                    <MuiTextField
                                        placeholder="0.00"
                                        type="number"
                                        fullWidth
                                        size="large"
                                        value={states.formik.values[ESaasFields.FRANCHISE].saas_fee_ac}
                                        onChange={e =>
                                            functions.handleFranchiseFieldChange("saas_fee_ac", e.target.value)
                                        }
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">SaaS Fee - DC (₹/kWh)</Typography>
                                    <MuiTextField
                                        placeholder="0.00"
                                        type="number"
                                        fullWidth
                                        size="large"
                                        value={states.formik.values[ESaasFields.FRANCHISE].saas_fee_dc}
                                        onChange={e =>
                                            functions.handleFranchiseFieldChange("saas_fee_dc", e.target.value)
                                        }
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">State</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder="Select State"
                                        options={states.franchiseStateOptions}
                                        loading={states.isFranchiseStatesFetching}
                                        value={states.formik.values[ESaasFields.FRANCHISE].state}
                                        onChange={(value: any) => functions.handleFranchiseStateChange(value)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">City</Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        disableClearable
                                        size="large"
                                        placeholder={
                                            states.formik.values[ESaasFields.FRANCHISE].state
                                                ? "Select City"
                                                : "Select State first"
                                        }
                                        options={states.franchiseCityOptions}
                                        loading={states.isFranchiseCitiesFetching}
                                        disabled={!states.formik.values[ESaasFields.FRANCHISE].state}
                                        value={states.formik.values[ESaasFields.FRANCHISE].city}
                                        onChange={value => functions.handleFranchiseFieldChange("city", value as any)}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Agreement Start Date</Typography>
                                    <MuiTextField
                                        type="date"
                                        fullWidth
                                        size="large"
                                        value={states.formik.values[ESaasFields.FRANCHISE].agreement_start_date}
                                        onChange={e =>
                                            functions.handleFranchiseFieldChange("agreement_start_date", e.target.value)
                                        }
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Tenure (months)</Typography>
                                    <MuiTextField
                                        placeholder="Enter tenure in months"
                                        type="number"
                                        fullWidth
                                        size="large"
                                        value={states.formik.values[ESaasFields.FRANCHISE].tenure}
                                        onChange={e => functions.handleFranchiseFieldChange("tenure", e.target.value)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Agreement End Date (auto)</Typography>
                                    <MuiTextField
                                        type="date"
                                        fullWidth
                                        disabled
                                        size="large"
                                        placeholder="Auto-calculated"
                                        value={states.formik.values[ESaasFields.FRANCHISE].agreement_end_date}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="label">Agreement</Typography>
                                    <FileUploader
                                        name="franchise_agreement"
                                        size="md"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(val: any) => {
                                            if (val?.length) {
                                                states.formik.setFieldValue(
                                                    `${ESaasFields.FRANCHISE}.agreement_url`,
                                                    "uploaded"
                                                )
                                            }
                                            functions.handleFranchiseFileUpload(val)
                                        }}
                                        title="Upload File"
                                        total={1}
                                        disabled={!states.franchiseIdFromBuyer}
                                        uploadedCount={
                                            states.formik.values[ESaasFields.FRANCHISE].agreement_url ? 1 : 0
                                        }
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Charger Details */}
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <Box
                        display={"flex"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        className="cursor-pointer"
                        onClick={() => functions.setShowChargerDetails(!states.showChargerDetails)}>
                        <Typography fontSize={"14px"} variant="label" color={"statiqOrange.main"}>
                            CHARGER DETAILS
                        </Typography>
                        <FontAwesomeIcon icon={states.showChargerDetails ? faAngleUp : faAngleDown} color="#8B8B8B" />
                    </Box>
                    {states.showChargerDetails && (
                        <ChargerDetailsTable
                            chargers={states.formik.values[ESaasFields.CHARGERS]}
                            onChargerChange={functions.handleChargerChange}
                            onBulkChargerUpdate={functions.handleBulkChargerUpdate}
                            chargerErrors={states.chargerErrors}
                        />
                    )}
                </Box>
            </Box>

            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                handleSubmit={functions.handleSubmit}
                isLoading={states.isUpdatingPageData}
            />
        </>
    )
}

export default SaaS
